/*
  CategoryCarousels — home-page "wheels".
  ───────────────────────────────────────────────────────────────────────────
  One horizontally-scrolling carousel per catalogue category, populated from
  GET /api/products/featured (products with featuredOnHome = true). Admins
  control what appears here via the "Feature on the homepage carousel" toggle
  and the category they assign in the product editor.
*/
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useCurrency } from '../../hooks/useCurrency';
import { useCategories } from '../../hooks/useCategories';
import CategoryIcon from '../CategoryIcon';
import styles from './CategoryCarousels.module.css';

function priceLabel(product, fmt) {
  const cents = (product.variants || [])
    .map(v => v.priceInCents)
    .filter(n => typeof n === 'number');
  if (!cents.length) return '';
  const min = Math.min(...cents);
  const multi = cents.length > 1 && Math.max(...cents) !== min;
  return (multi ? 'from ' : '') + fmt(min);
}

function Wheel({ cat, products, fmt }) {
  const trackRef = useRef(null);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(260, el.clientWidth * 0.8), behavior: 'smooth' });
  };

  return (
    <div className={styles.wheel}>
      <div className={styles.head}>
        <div className={styles.title}>
          <span className={styles.chip} style={{ background: cat.grad }}>
            <CategoryIcon category={cat.value} style={{ width: 28, height: 28, color: cat.ink }} />
          </span>
          <div className={styles.titleText}>
            <h3>{cat.label}</h3>
            <p>{cat.blurb}</p>
          </div>
        </div>
        <div className={styles.controls}>
          <Link className={styles.shopAll} to="/shop">Shop all →</Link>
          <button type="button" className={styles.arrow} aria-label={`Scroll ${cat.label} left`}
            onClick={() => scrollBy(-1)}>‹</button>
          <button type="button" className={styles.arrow} aria-label={`Scroll ${cat.label} right`}
            onClick={() => scrollBy(1)}>›</button>
        </div>
      </div>

      <div className={styles.track} ref={trackRef}>
        {products.map((p) => (
          <Link className={styles.card} to={`/shop/${p.slug}`} key={p.id}>
            <div className={styles.thumb} style={{ background: cat.grad }}>
              {p.imageUrl ? (
                <img src={p.imageUrl} alt={p.name}
                  onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
              ) : null}
              <span className={styles.glyph} style={{ display: p.imageUrl ? 'none' : 'flex', color: cat.ink }}>
                <CategoryIcon category={cat.value} style={{ width: 56, height: 56 }} />
              </span>
              <span className={styles.badge} style={{ color: cat.ink }}>{cat.label}</span>
            </div>
            <div className={styles.body}>
              <div className={styles.cat}>{cat.label}</div>
              <h4 className={styles.name}>{p.name}</h4>
              <div className={styles.foot}>
                <span className={styles.price}>{priceLabel(p, fmt)}</span>
                <span className={styles.go}>Shop <i>→</i></span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function CategoryCarousels() {
  const { fmt } = useCurrency({ usd: true });
  const { categories } = useCategories();
  const [byCategory, setByCategory] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/products/featured')
      .then(res => { if (alive) setByCategory(res.data.byCategory || {}); })
      .catch(() => { if (alive) setByCategory({}); });
    return () => { alive = false; };
  }, []);

  if (!byCategory) return null; // silent until loaded

  // Only render categories that actually have featured products.
  const wheels = categories
    .map(cat => ({ cat, products: byCategory[cat.value] || [] }))
    .filter(w => w.products.length > 0);

  if (wheels.length === 0) return null;

  return (
    <section className={styles.section} id="categories" aria-label="Shop by category">
      {wheels.map(({ cat, products }) => (
        <Wheel key={cat.value} cat={cat} products={products} fmt={fmt} />
      ))}
    </section>
  );
}
